import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useRef, useEffect, useState, useCallback, useSyncExternalStore, lazy, Suspense } from 'react';
import { useAppSelector, useAppDispatch } from '@/store/hooks';
import { clearEnv } from '@/store/slices/envsSlice';
import { loadNotifications, markNotificationsChecked, type NotificationItem } from '@/store/slices/notificationsSlice';
import {
  clearCampaignCache,
  getHeavyIndexedCacheMinRemainingMs,
} from '@/lib/api/campaign.api';
import {
  INDEXED_CACHE_TTL_MS,
  formatCacheCountdownMs,
  getInFlightHeavyRefreshCount,
  subscribeHeavyRefresh,
} from '@/lib/utils/indexedDbCache';
import { CachedApiTargetsList } from '@/components/CachedApiTargetsList';
import { useHeavyCacheWarming } from '@/lib/hooks/useHeavyCacheWarming';
import { getHeaderLinks, type MenuLink } from '@/lib/constants/headerLinks';
import { changeLanguage } from '@/lib/i18n';
import { useI18nText } from '@/lib/utils/i18nText';
import { getSocket } from '@/lib/socket';
import {
  getStoredNotificationSocketEnabled,
  setNotificationSocketEnabled,
} from '@/lib/utils/notificationSocketPref';
import { detectBeTargetEnvFromBrowser } from '@/lib/config/runtimeEnv';
import Tooltip from '@/components/Tooltip';
import { logout } from '@/lib/api/auth.api';
import { loadOrganizations, switchOrganization, type Organization } from '@/lib/api/organizations.api';
import { ChannelDrawer } from '@/components/ChannelDrawer';
import { useChannelDrawer } from '@/lib/hooks/useChannelDrawer';
import {
  getStoredShortcut,
  SHORTCUT_CHANGE_EVENT,
  matchesShortcut,
  formatShortcut,
  isCapturingMode,
  type SearchShortcut,
} from '@/lib/utils/searchShortcut';
import { ClockPopover } from '@/components/ClockPopover';
import { GgcCheckbox } from '@/components/ui/ggc/checkbox';
import { GgcEmptyState } from '@/components/ui/ggc/empty-state';

const QuickSearchDialog = lazy(() => import('./QuickSearchDialog'));

/** Background-fetch the QuickSearchDialog chunk so the first keyboard-shortcut open
 *  is instant. Called once when the user is logged in. */
function prefetchQuickSearchDialog(): void {
  void import('./QuickSearchDialog');
}

/** fab: 접두사면 FontAwesome Brands 아이콘 사용 */
function getIconClass(icon: string): string {
  return icon.startsWith('fab:') ? `fab fa-${icon.slice(4)} fa-fw` : `fa fa-${icon} fa-fw`;
}

/** Matches the ≤768px breakpoint used app-wide for the mobile layout (base.css/components.css). */
const MOBILE_MEDIA_QUERY = '(max-width: 768px)';

/**
 * Left-nav dropdowns (Campaigns/Influencers/Tools/Payments) use `menuStyle: 'hover'`
 * on desktop — hover reveals the submenu, and clicking the button itself jumps
 * straight to the first sublink (a deliberate "the hover already showed you the
 * choices" shortcut). Touch devices have no real hover, so that same tap skips
 * straight past the submenu to the first item, with no way to see or reach the
 * others. Below 768px, use `'click'` instead so the first tap opens the submenu
 * (and a second tap on an item — or a click outside — is what navigates/closes).
 */
function useNavMenuStyle(): 'click' | 'hover' {
  const [isMobile, setIsMobile] = useState(
    () => typeof window !== 'undefined' && window.matchMedia(MOBILE_MEDIA_QUERY).matches,
  );
  useEffect(() => {
    const mql = window.matchMedia(MOBILE_MEDIA_QUERY);
    const handler = (e: MediaQueryListEvent) => setIsMobile(e.matches);
    mql.addEventListener('change', handler);
    return () => mql.removeEventListener('change', handler);
  }, []);
  return isMobile ? 'click' : 'hover';
}

/** 기존 gc-admin UI: navbar-inverse, MenuList 드롭다운, 유저 메뉴 */
export default function Header() {
  const currentUser = useAppSelector((s) => s.envs.currentUser);
  const dispatch = useAppDispatch();
  const { t } = useI18nText();
  const navigate = useNavigate();
  const location = useLocation();
  const toAccessAllFeatures = !!currentUser?.permissions?.toAccessAllFeatures;
  const isSocialTrendTrial = false; // TODO: checkSocialTrendTrial from store
  const role = currentUser?.role ?? 'guest';

  const { admin, guest } = getHeaderLinks({
    user: currentUser,
    isSocialTrendTrial,
    toAccessAllFeatures,
  });

  const userLinks = role === 'admin' ? admin : guest;

  const menuStyle = useNavMenuStyle();

  // Proactive heavy-cache warming (admin only) — refreshes expired caches in the background
  // while the tab is visible and the person is active, so navigation rarely hits stale data.
  useHeavyCacheWarming(role === 'admin');

  // SPA 내에서 로그아웃 처리: BE /logout 호출 → Redux 초기화 → /login 이동
  const handleLogout = useCallback(async (e: React.MouseEvent) => {
    e.preventDefault();
    try {
      await logout();
    } catch { /* BE 로그아웃 실패해도 FE 상태는 초기화 */ }
    dispatch(clearEnv());
    navigate('/login', { replace: true });
  }, [dispatch, navigate]);

  // WebSocket 기반 실시간 알림 구독 (admin 전용)
  const notifications = useAppSelector((s) => s.notifications.items);
  useEffect(() => {
    if (role !== 'admin') return;

    dispatch(loadNotifications());

    const socket = getSocket();
    if (!socket) return;

    const handler = () => {
      dispatch(loadNotifications());
    };
    socket.on('NEW_NOTIFICATION', handler);
    return () => {
      socket.off('NEW_NOTIFICATION', handler);
    };
  }, [dispatch, role]);

  // Configurable search shortcut — reads from localStorage, updates via custom event
  const [searchShortcut, setSearchShortcut] = useState<SearchShortcut>(getStoredShortcut);

  useEffect(() => {
    const handler = (e: Event) => {
      setSearchShortcut((e as CustomEvent<SearchShortcut>).detail);
    };
    window.addEventListener(SHORTCUT_CHANGE_EVENT, handler);
    return () => window.removeEventListener(SHORTCUT_CHANGE_EVENT, handler);
  }, []);

  // 검색 Drawer 열림 상태
  const [searchOpen, setSearchOpen] = useState(false);
  const { drawer, drawerEntered, closeDrawer, openChatDrawer, openPanelDrawer } = useChannelDrawer();
  const [clearingCache, setClearingCache] = useState(false);
  /** 무거운 API IndexedDB 캐시 최소 잔여 TTL(ms). undefined=미조회, null=해당 캐시 없음·만료됨 */
  const [heavyCacheRemainMs, setHeavyCacheRemainMs] = useState<number | null | undefined>(
    undefined,
  );
  /**
   * A heavy-cache network refresh is in flight (blocking or background). Drives the icon
   * spin, so it turns only while data is actually being fetched — not merely because an
   * entry has expired and is waiting for someone to read it.
   */
  const inFlightHeavyRefreshes = useSyncExternalStore(
    subscribeHeavyRefresh,
    getInFlightHeavyRefreshCount,
    getInFlightHeavyRefreshCount,
  );
  const backgroundRefreshing = inFlightHeavyRefreshes > 0;

  // Prefetch the QuickSearchDialog chunk after mount so the first open is instant.
  useEffect(() => {
    if (!currentUser) return;
    prefetchQuickSearchDialog();
  }, [currentUser]);

  // Configurable keyboard shortcut to toggle the Search drawer (logged-in users only).
  // The shortcut is read from localStorage via searchShortcut state and updates live
  // when the user changes it in /settings without requiring a page reload.
  // Gated by isCapturingMode() so the ShortcutCapture UI in /settings can intercept
  // keypresses without accidentally opening the drawer.
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (isCapturingMode()) return;
      if (matchesShortcut(e, searchShortcut)) {
        e.preventDefault();
        if (currentUser) setSearchOpen((prev) => !prev);
      }
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [currentUser, searchShortcut]);

  // Green header + "Staging Environment" chip signal the STAGING backend.
  // Driven by the BE target (staging → green/chip, production → black/no chip),
  // NOT the BE-reported env (unreliable when unauthenticated) and NOT the
  // local/deployed distinction: prod.localhost (points at the prod BE) must be
  // black, staging deploy must be green. detectBeTargetEnvFromBrowser is
  // hostname/v2-target based → auth-independent, so the chip never shows on
  // production regardless of login state. (Favicon uses the runtime env instead
  // — grey on local — set at startup in main.tsx.)
  const isStagingBe = detectBeTargetEnvFromBrowser() === 'staging';

  const handleClearCache = useCallback(async () => {
    if (clearingCache) {
      return;
    }

    setClearingCache(true);
    try {
      await clearCampaignCache();
    } finally {
      window.location.reload();
    }
  }, [clearingCache]);

  useEffect(() => {
    if (role !== 'admin') {
      return;
    }

    let cancelled = false;
    const tick = async () => {
      const ms = await getHeavyIndexedCacheMinRemainingMs();
      if (!cancelled) setHeavyCacheRemainMs(ms);
    };

    void tick();
    const id = window.setInterval(() => void tick(), 1000);
    return () => {
      cancelled = true;
      window.clearInterval(id);
    };
  }, [role]);

  const heavyCacheCountdownText =
    heavyCacheRemainMs === undefined
      ? ''
      : heavyCacheRemainMs === null
        ? formatCacheCountdownMs(INDEXED_CACHE_TTL_MS)
        : formatCacheCountdownMs(heavyCacheRemainMs);

  const [cachePopoverOpen, setCachePopoverOpen] = useState(false);
  const [cacheDetailOpen, setCacheDetailOpen] = useState(false);
  const cachePopoverRef = useRef<HTMLLIElement>(null);
  const cacheLeaveTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => () => { if (cacheLeaveTimer.current) clearTimeout(cacheLeaveTimer.current); }, []);

  const handleCacheMouseEnter = () => {
    if (cacheLeaveTimer.current) { clearTimeout(cacheLeaveTimer.current); cacheLeaveTimer.current = null; }
    setCachePopoverOpen(true);
  };
  const handleCacheMouseLeave = () => {
    cacheLeaveTimer.current = setTimeout(() => setCachePopoverOpen(false), 200);
  };
  // Mobile (≤768px, mirrors base.css's breakpoint): the popover was hover-only
  // (onMouseEnter/onMouseLeave above), which touch never fires — the info was
  // unreachable and, once open some other way, unclosable. Tap-toggle it
  // instead; desktop hover behavior is untouched since this only acts below
  // the breakpoint.
  const handleCacheButtonClick = () => {
    if (window.matchMedia('(max-width: 768px)').matches) {
      setCachePopoverOpen((o) => !o);
    }
  };
  // Close on outside tap — the mouseleave timer above never fires on touch,
  // so without this the popover, once opened, would stay open until the next
  // toggle tap. Same idiom as ClockPopover's outside-click handler.
  useEffect(() => {
    if (!cachePopoverOpen) return;
    const handleClick = (e: MouseEvent) => {
      if (cachePopoverRef.current && !cachePopoverRef.current.contains(e.target as Node)) {
        setCachePopoverOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [cachePopoverOpen]);

  // Mobile header: the whole navbar-right icon cluster (clock/search/cache/
  // notifications/language/user) collapses into one hamburger toggle below
  // 768px (see base.css) and opens as a full-width dropdown panel.
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const mobileMenuRef = useRef<HTMLUListElement>(null);

  useEffect(() => {
    if (!mobileMenuOpen) return;
    const handler = (e: MouseEvent) => {
      if (mobileMenuRef.current && !mobileMenuRef.current.contains(e.target as Node)) {
        setMobileMenuOpen(false);
      }
    };
    document.addEventListener('click', handler);
    return () => document.removeEventListener('click', handler);
  }, [mobileMenuOpen]);

  // Close on route change so navigating from a link inside the panel
  // (Settings, notification item, logout, …) doesn't leave it open.
  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location.pathname]);

  return (
    <nav className={`navbar navbar-inverse navbar-fixed-top container-fluid header-navbar ${isStagingBe ? 'header-navbar--staging !bg-[var(--staging-header-bg,#00897b)]' : 'header-navbar--prod'}`}>
      <div className="navbar-header">
        <Link to="/" className="navbar-brand" aria-label={t('GGContent Admin home', 'GGContent 어드민 홈')}>
          <div className="svgs logo" aria-hidden />
        </Link>
      </div>

      {currentUser && (
        <ul className="nav navbar-nav navbar-left">
          {userLinks.map((link, index) =>
            link.links ? (
              <NavDropdown key={`dd-${index}`} link={link} user={currentUser} menuStyle={menuStyle} />
            ) : (
              <NavItem key={index} link={link} user={currentUser} />
            )
          )}
          {isStagingBe && (
            <li className="header-staging-chip-wrap">
              <span className="header-staging-chip">
                <span className="header-staging-chip__full">{t('Staging Environment', '스테이징 환경')}</span>
                <span className="header-staging-chip__short">{t('Staging', '스테이징')}</span>
              </span>
            </li>
          )}
        </ul>
      )}

      {currentUser && (
        <button
          type="button"
          className="navbar-mobile-toggle"
          aria-label={t('Menu', '메뉴')}
          aria-expanded={mobileMenuOpen}
          onClick={(e) => {
            // The toggle button is a sibling of the panel <ul>, not a descendant of
            // it — so without this, the very click that OPENS the panel bubbles on
            // to document, where the outside-click effect below (now subscribed,
            // since it re-runs synchronously enough on some engines/timings) reads
            // it as an outside click and immediately closes what it just opened.
            // Stopping propagation here means this click never reaches that
            // listener at all, independent of any such timing.
            e.stopPropagation();
            setMobileMenuOpen((o) => !o);
          }}
        >
          <i className={`fa fa-${mobileMenuOpen ? 'times' : 'bars'}`} aria-hidden />
        </button>
      )}

      <ul ref={mobileMenuRef} className={`nav navbar-nav navbar-right${mobileMenuOpen ? ' navbar-right--mobile-open' : ''}`}>
        {currentUser && (
          <>
            {role === 'admin' && (
              <li className="navbar-icon-item navbar-clock-item">
                <ClockPopover />
              </li>
            )}
            {role === 'admin' && (
              <li className="navbar-icon-item navbar-search-item">
                <Tooltip content={`Search (${formatShortcut(searchShortcut)})`} placement="bottom">
                  <button
                    type="button"
                    className="navbar-icon-btn !text-white"
                    aria-label={t('Search', '검색')}
                    onClick={() => { setSearchOpen((prev) => !prev); setMobileMenuOpen(false); }}
                  >
                    <i className="fa fa-search text-[22px]" />
                  </button>
                </Tooltip>
              </li>
            )}
            {/* Mobile-only row break: forces cache/bell onto a fresh line instead of
                packing onto the clock+search line just because they'd technically
                fit — flex-wrap groups items by hypothetical (pre-grow) size, so
                without this a narrow cache/bell pair could still squeeze in
                before clock's flex-grow gets to push search to the row's right
                edge. Renders as nothing (display:none) outside the mobile panel. */}
            {role === 'admin' && <li className="navbar-right-break" aria-hidden />}
            {role === 'admin' && (
              <li
                ref={cachePopoverRef}
                className="navbar-icon-item navbar-cache-item relative"
                onMouseEnter={handleCacheMouseEnter}
                onMouseLeave={handleCacheMouseLeave}
              >
                {/* Trigger (icon + countdown) lives in its own row wrapper so the
                    popover below is a normal block sibling instead of a third
                    flex item squeezed into this row on mobile (see base.css
                    .navbar-cache-trigger — the <li> itself no longer flexes). */}
                <div className="navbar-cache-trigger flex flex-col items-center justify-center gap-0 !py-0.5">
                  <button
                    type="button"
                    className="navbar-icon-btn !text-white !py-0 !pb-0"
                    disabled={clearingCache}
                    aria-label={t('Cache info', '캐시 정보')}
                    onClick={handleCacheButtonClick}
                  >
                    <i
                      className={`fa fa-refresh inline-block origin-center transition-transform duration-200 ${
                        clearingCache || backgroundRefreshing ? 'animate-spin' : ''
                      }`}
                      aria-hidden
                    />
                  </button>
                  <span className="inline-flex min-w-[2.25rem] cursor-default justify-center tabular-nums leading-none text-white/80 select-none ggc-text-caption-s">
                    {heavyCacheCountdownText || '\u00a0'}
                  </span>
                </div>
                {cachePopoverOpen && (
                  <div className="navbar-cache-popover absolute right-0 top-full mt-1 w-[340px] rounded-ggc-md border border-ggc-border-gray bg-white text-ggc-text-primary shadow-xl z-[1100] p-4 text-xs leading-snug space-y-2">
                    <p className="font-semibold text-ggc-text-primary">
                      API Cache(IndexedDB)
                    </p>
                    {heavyCacheCountdownText && (() => {
                      const ratio = heavyCacheRemainMs == null ? 0 : heavyCacheRemainMs / INDEXED_CACHE_TTL_MS;
                      const pct = Math.round(ratio * 100);
                      const barColor = heavyCacheRemainMs == null
                        ? 'bg-gray-300'
                        : ratio > 0.6 ? 'bg-green-400'
                        : ratio > 0.3 ? 'bg-amber-400'
                        : 'bg-red-400';
                      return (
                        <div>
                          <div className="flex items-center justify-between mb-1.5">
                            <span className="ggc-text-caption-m text-ggc-text-gray">{t('Expires in', 'Expires in')}</span>
                            <span className="font-mono text-[12px] font-semibold text-ggc-text-primary">
                              <i className="fa fa-clock text-ggc-disabled mr-1" />
                              {heavyCacheCountdownText}
                            </span>
                          </div>
                          <div className="h-1.5 w-full rounded-ggc-pill bg-ggc-bg-base overflow-hidden">
                            <div
                              className={`h-full rounded-ggc-pill transition-all duration-1000 ${barColor}`}
                              style={{ width: `${pct}%` }}
                            />
                          </div>
                        </div>
                      );
                    })()}
                    {/* Collapsible detail section — what the store actually holds right now,
                        i.e. exactly what "Refresh now" clears (see clearAllHeavyCaches). */}
                    <div>
                      <button
                        type="button"
                        className="flex items-center gap-1 text-[11px] text-ggc-disabled hover:text-ggc-text-gray transition-colors"
                        onClick={() => setCacheDetailOpen((o) => !o)}
                      >
                        <i className={`fa fa-chevron-${cacheDetailOpen ? 'up' : 'down'} text-[9px]`} />
                        {t('Cached data sources (5-minute TTL each)', '캐시 대상 데이터 (각각 유효 시간 5분)')}
                      </button>
                      {cacheDetailOpen && <CachedApiTargetsList className="mt-1.5" />}
                    </div>
                    <div className="border-t border-ggc-border-gray pt-2">
                      <button
                        type="button"
                        className="w-full rounded-md bg-gray-800 px-3 py-1.5 text-[12px] font-medium text-white hover:bg-gray-700 disabled:opacity-50 transition-colors flex items-center justify-center gap-2"
                        onClick={() => { setCachePopoverOpen(false); void handleClearCache(); }}
                        disabled={clearingCache}
                      >
                        <i className={`fa fa-refresh ${clearingCache ? 'animate-spin' : ''}`} />
                        {clearingCache
                          ? t('Clearing...', '삭제 중...')
                          : t('Refresh now (clear cache & reload)', '지금 새로고침 (캐시 삭제 후 재로드)')}
                      </button>
                    </div>
                  </div>
                )}
              </li>
            )}
            {role === 'admin' && (
              <NotificationBell
                notifications={notifications}
                onMarkChecked={(ids) => dispatch(markNotificationsChecked(ids))}
              />
            )}
            <LanguageSwitch />
            <UserDropdown
              user={currentUser}
              onLogout={handleLogout}
            />
          </>
        )}
      </ul>

      {!currentUser && (
        <>
          <div className="navbar-spacer" aria-hidden />
          <ul className="nav navbar-nav navbar-right">
            <LanguageSwitch />
            <li>
              <Link to="/login" className="nav-link">
                {t('Sign In', '로그인')} 
              </Link>
            </li>
          </ul>
        </>
      )}

      {/* Quick Search Dialog: 상단에서 슬라이드 다운 */}
      {searchOpen && currentUser && (
        <Suspense fallback={null}>
          <QuickSearchDialog
            onClose={() => setSearchOpen(false)}
            onChatSystemClick={(userId) => { setSearchOpen(false); openChatDrawer(userId); }}
            onChannelDetailsClick={(userId) => { setSearchOpen(false); openPanelDrawer(userId); }}
          />
        </Suspense>
      )}
      <ChannelDrawer drawer={drawer} drawerEntered={drawerEntered} onClose={closeDrawer} />
    </nav>
  );
}

function resolveHref(link: MenuLink, user: unknown): string {
  if (!link.href) return '#';
  if (typeof link.href === 'function') return link.href(user);
  return link.href;
}

function UserDropdown({
  user,
  onLogout,
}: {
  user: { avatar?: string; name?: string; email?: string; permissions?: Record<string, boolean> };
  onLogout: (e: React.MouseEvent) => void;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLLIElement>(null);
  const { t } = useI18nText();

  const currentOrg = useAppSelector((s) => s.envs.organization) as Organization | null;
  const role = useAppSelector((s) => (s.envs.currentUser as { role?: string } | null)?.role ?? '');
  const canSwitchOrg = !!user?.permissions?.toManageAllOrganizations;
  /** GGC 내부 직원(@ggcontent.com)만 노출. 게스트·외부 조직 차단. */
  const canSeeSettings = role.toLowerCase() === 'admin'
    && /@ggcontent\.com$/i.test(user?.email ?? '');

  const [showOrgModal, setShowOrgModal] = useState(false);
  const [allOrgs, setAllOrgs] = useState<Organization[]>([]);
  const [selectedOrgId, setSelectedOrgId] = useState('');
  const [orgLoading, setOrgLoading] = useState(false);
  const [switching, setSwitching] = useState(false);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('click', handler);
    return () => document.removeEventListener('click', handler);
  }, []);

  const handleOpenSwitchOrg = async (e: React.MouseEvent) => {
    e.preventDefault();
    setOpen(false);
    setShowOrgModal(true);
    setSelectedOrgId(currentOrg?._id ?? '');

    if (allOrgs.length === 0) {
      setOrgLoading(true);
      try {
        const list = await loadOrganizations();
        setAllOrgs(list);
      } catch {
        // 로드 실패 시 빈 목록 유지
      } finally {
        setOrgLoading(false);
      }
    }
  };

  const handleSwitchOrg = async () => {
    if (!selectedOrgId || selectedOrgId === currentOrg?._id) return;
    setSwitching(true);
    try {
      await switchOrganization(selectedOrgId);
      window.location.href = '/';
    } catch {
      setSwitching(false);
    }
  };

  return (
    <>
      <li ref={ref} className={`dropdown ${open ? 'open' : ''}`}>
        <button
          type="button"
          className="dropdown-toggle"
          onClick={() => setOpen((o) => !o)}
        >
          <img
            src={user?.avatar ?? 'https://gravatar.com/avatar/?s=60&d=retro'}
            alt=""
          />
          <span className="navbar-user-name">{user?.name ?? user?.email ?? t('Guest', '게스트')}</span>
          <span className="caret" />
        </button>
        <ul className="dropdown-menu">
          {canSeeSettings && (
            <li>
              <Link to="/settings" onClick={() => setOpen(false)}>
                <i className="fa fa-cog" /> {t('Settings', '설정')}
              </Link>
            </li>
          )}
          <li>
            <Link to="/change_password" onClick={() => setOpen(false)}>
              <i className="fa fa-lock" /> {t('Change password', '비밀번호 변경')}
            </Link>
          </li>
          {canSwitchOrg && (
            <li>
              <a href="#" onClick={handleOpenSwitchOrg}>
                <i className="fa fa-refresh" /> {t('Switch organization', '조직 전환')}
              </a>
            </li>
          )}
          <li className="dropdown-menu-divider" role="separator" />
          <li>
            <a href="/logout" onClick={onLogout}>
              <i className="fa fa-sign-out" /> {t('Logout', '로그아웃')}
            </a>
          </li>
        </ul>
      </li>

      {/* Switch Organization 모달 */}
      {showOrgModal && (
        <div className="switch-org-overlay" onClick={() => !switching && setShowOrgModal(false)}>
          <div className="switch-org-modal" onClick={(e) => e.stopPropagation()}>
            <h3 className="switch-org-title">{t('Switch Organization', '조직 전환')}</h3>

            {orgLoading ? (
              <div className="switch-org-loading">
                <i className="fa fa-spinner fa-spin" /> {t('Loading...', '로딩 중...')}
              </div>
            ) : allOrgs.length === 0 ? (
              <div className="switch-org-empty">
                {t('No organizations found', '조직을 찾을 수 없습니다')}
              </div>
            ) : (
              <div className="switch-org-list">
                {allOrgs.map((org) => {
                  const isCurrent = org._id === currentOrg?._id;
                  return (
                    <label key={org._id} className={`switch-org-item${isCurrent ? ' switch-org-item--current' : ''}`}>
                      <input
                        type="radio"
                        name="orgSwitch"
                        value={org._id}
                        checked={selectedOrgId === org._id}
                        disabled={isCurrent}
                        onChange={() => setSelectedOrgId(org._id)}
                      />
                      <span className="switch-org-name">
                        {org.name}
                        {isCurrent && <span className="switch-org-current-badge">{t('(current)', '(현재)')}</span>}
                      </span>
                    </label>
                  );
                })}
              </div>
            )}

            <div className="switch-org-actions">
              <button
                className="switch-org-btn switch-org-btn--cancel"
                onClick={() => setShowOrgModal(false)}
                disabled={switching}
              >
                {t('Cancel', '취소')}
              </button>
              <button
                className="switch-org-btn switch-org-btn--ok"
                onClick={() => void handleSwitchOrg()}
                disabled={!selectedOrgId || selectedOrgId === currentOrg?._id || switching}
              >
                {switching ? <><i className="fa fa-spinner fa-spin" /> {t('Switching...', '전환 중...')}</> : t('OK', '확인')}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

function NavItem({
  link,
  user,
}: {
  link: MenuLink;
  user: unknown;
}) {
  const href = resolveHref(link, user);
  const isExternal = link.target === '_blank';
  const { t } = useI18nText();
  const label = t(link.text, link.textKo);

  if (link.disabled) {
    const tip = t(link.disabledTooltip ?? '', link.disabledTooltipKo);
    return (
      <li>
        <Tooltip content={tip} placement="bottom">
          <span className="nav-link-disabled">
            <i className={`fa fa-${link.icon}`} /> <span className="navbar-primary-label">{label}</span>
          </span>
        </Tooltip>
      </li>
    );
  }

  if (isExternal) {
    return (
      <li>
        <a href={href} target="_blank" rel="noopener noreferrer">
          <i className={`fa fa-${link.icon}`} /> <span className="navbar-primary-label">{label}</span>
        </a>
      </li>
    );
  }

  return (
    <li>
      <Link to={href}>
        <i className={`fa fa-${link.icon}`} /> <span className="navbar-primary-label">{label}</span>
      </Link>
    </li>
  );
}

function LanguageSwitch() {
  const [open, setOpen] = useState(false);
  // Derive lang from i18n so it reacts to language changes from any source
  // (e.g. the /settings page) without needing a page reload.
  const { lang } = useI18nText();
  const ref = useRef<HTMLLIElement>(null);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('click', handler);
    return () => document.removeEventListener('click', handler);
  }, []);

  const handleSelect = (lng: 'en' | 'ko') => {
    changeLanguage(lng).then(() => {
      setOpen(false);
    });
  };

  return (
    <li ref={ref} className={`navbar-icon-item dropdown ${open ? 'open' : ''}`}>
      <button
        type="button"
        className="navbar-icon-btn lang-switch-btn"
        onClick={() => setOpen((o) => !o)}
        title="Language"
      >
        <i className="fa fa-globe" />
        <span className="lang-label">{lang === 'ko' ? 'KO' : 'EN'}</span>
        <span className="caret" />
      </button>
      <ul className="dropdown-menu lang-dropdown-menu">
        <li className={lang === 'en' ? 'active' : ''}>
          <button type="button" className="lang-option" onClick={() => handleSelect('en')}>
            <span className="lang-label">English</span>
          </button>
        </li>
        <li className={lang === 'ko' ? 'active' : ''}>
          <button type="button" className="lang-option" onClick={() => handleSelect('ko')}>
            <span className="lang-label">한국어</span>
          </button>
        </li>
      </ul>
    </li>
  );
}

function NotificationBell({
  notifications,
  onMarkChecked,
}: {
  notifications: NotificationItem[];
  onMarkChecked: (ids: string[]) => void;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLLIElement>(null);
  const { t } = useI18nText();

  // Opt out of the realtime Socket.IO connection (persisted in localStorage,
  // applied by AuthContext) for people who don't actively rely on live push
  // and would rather avoid the recurring connect/reconnect polling.
  const [socketEnabled, setSocketEnabled] = useState(getStoredNotificationSocketEnabled);
  const handleToggleSocket = (next: boolean) => {
    setSocketEnabled(next);
    setNotificationSocketEnabled(next);
  };

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('click', handler);
    return () => document.removeEventListener('click', handler);
  }, []);

  const messageNotifications = notifications.filter((n) => n.kind === 'message');
  const count = notifications.length;

  const handleMarkAllRead = () => {
    const ids = notifications.map((n) => n.id ?? n._id ?? '').filter(Boolean);
    if (ids.length > 0) onMarkChecked(ids);
    setOpen(false);
  };

  return (
    <li ref={ref} className={`navbar-icon-item dropdown ${open ? 'open' : ''}`}>
      <Tooltip
        content={socketEnabled ? t('Notifications', '알림') : t('Notifications: Off', '알림: 꺼진 상태')}
        placement="bottom"
      >
        <button
          type="button"
          className="navbar-icon-btn notification-bell-btn !text-white"
          aria-label={socketEnabled ? t('Notifications', '알림') : t('Notifications: Off', '알림: 꺼진 상태')}
          onClick={() => setOpen((o) => !o)}
        >
          <i className={`fa ${socketEnabled ? 'fa-bell' : 'fa-bell-slash opacity-20'}`} />
          {count > 0 && (
            <span className="notification-badge">{count > 10 ? '10+' : count}</span>
          )}
        </button>
      </Tooltip>

      {open && (
        <div className="dropdown-menu notification-dropdown">
          <div className="notification-dropdown-header">
            <span className="notification-dropdown-title">
              <i className="fa fa-bell" aria-hidden />
              {t('Notifications', '알림')}
            </span>
            <div className="notification-dropdown-header-actions">
              <Tooltip
                content={t(
                  'Live updates — turn off to disconnect the notification socket and stop background reconnects.',
                  '실시간 업데이트 — 끄면 알림 소켓 연결을 끊고 백그라운드 재연결도 하지 않습니다.',
                )}
                placement="bottom"
              >
                <span className="notification-realtime-toggle">
                  <GgcCheckbox
                    checked={socketEnabled}
                    onChange={(e) => handleToggleSocket(e.target.checked)}
                    label={t('Live', '실시간')}
                  />
                </span>
              </Tooltip>
              {count > 0 && (
                <button
                  type="button"
                  className="notification-mark-all"
                  onClick={handleMarkAllRead}
                >
                  {t('Mark all read', '모두 읽음')}
                </button>
              )}
            </div>
          </div>

          <div className="notification-dropdown-body">
            {count === 0 ? (
              <div className="notification-empty">
                <GgcEmptyState
                  style={{ minHeight: 0 }}
                  icon={<i className="fa fa-bell-slash-o" aria-hidden />}
                  title={t('No new notifications', '새 알림이 없습니다')}
                />
              </div>
            ) : (
              <ul className="notification-list">
                {messageNotifications.map((n) => {
                  const nId = n.id ?? n._id ?? '';
                  return (
                    <li key={nId} className="notification-item">
                      <Link
                        to={`/influencer/${n.details.userId}/campaigns/${n.details.campaignId}`}
                        onClick={() => {
                          onMarkChecked([nId]);
                          setOpen(false);
                        }}
                      >
                        <i className="fa fa-envelope notification-item-icon" />
                        <div className="notification-item-content">
                          <span className="notification-item-user">{n.details.userTitle}</span>
                          <span className="notification-item-title">{n.title}</span>
                        </div>
                      </Link>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        </div>
      )}
    </li>
  );
}

function NavDropdown({
  link,
  user,
  menuStyle = 'click',
}: {
  link: MenuLink;
  user: { permissions?: Record<string, boolean> };
  menuStyle?: 'click' | 'hover';
}) {
  const [open, setOpen] = useState(false);
  // Tracks which submenu item the mouse is actually over, so the "default action"
  // hint on the first item (see firstSublink below) yields to a real :hover the
  // moment the user moves onto a specific item — otherwise both could appear
  // highlighted at once, the same double-highlight look the hint is meant to avoid.
  const [hoveredSubIdx, setHoveredSubIdx] = useState<number | null>(null);
  const ref = useRef<HTMLLIElement>(null);
  const leaveTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const { t } = useI18nText();
  const navigate = useNavigate();

  useEffect(() => {
    if (menuStyle !== 'click') return;
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('click', handler);
    return () => document.removeEventListener('click', handler);
  }, [menuStyle]);

  useEffect(() => () => { if (leaveTimer.current) clearTimeout(leaveTimer.current); }, []);

  const sublinks = link.sort && link.links
    ? [...link.links].sort((a, b) => a.text.localeCompare(b.text))
    : link.links ?? [];

  const filtered = sublinks.filter((sublink) => {
    if (sublink.permissions) {
      const found = sublink.permissions.filter((p) => user?.permissions?.[p]);
      return found.length === sublink.permissions.length;
    }
    return true;
  });
  // The button itself navigates straight to this item when clicked (hover menuStyle) —
  // hinted in the dropdown by highlighting it like a real :hover (see showActiveHint below).
  const firstSublink = filtered.find((s) => !s.separator && !s.sectionTitle);
  const isWideDropdownMenu = ['tools'].some((text) => link.text.toLowerCase().includes(text));
  const dropdownClassName = `dropdown ${open ? 'open' : ''}${isWideDropdownMenu ? ' header-dropdown--wide' : ''}`;

  const hoverProps = menuStyle === 'hover'
    ? {
        onMouseEnter: () => {
          if (leaveTimer.current) { clearTimeout(leaveTimer.current); leaveTimer.current = null; }
          setOpen(true);
        },
        onMouseLeave: () => {
          // Clear hoveredSubIdx only once the dropdown actually closes, not on this
          // leave itself — otherwise a brief moment where the mouse is between
          // items (still within the still-open dropdown) would flash the hint back
          // onto the first item right before everything closes.
          leaveTimer.current = setTimeout(() => {
            setOpen(false);
            setHoveredSubIdx(null);
          }, 100);
        },
      }
    : {};

  return (
    <li ref={ref} className={dropdownClassName} {...hoverProps}>
      <button
        type="button"
        className="dropdown-toggle"
        onClick={menuStyle === 'click'
          ? () => setOpen((o) => !o)
          : () => {
              if (!firstSublink) return;
              if (firstSublink.target === '_blank') {
                window.open(typeof firstSublink.href === 'function' ? firstSublink.href(user) : firstSublink.href ?? '#', '_blank', 'noopener,noreferrer');
              } else {
                navigate(resolveHref(firstSublink, user));
              }
            }}
      >
        <i className={`fa fa-${link.icon}`} /> <span className="navbar-dropdown-label">{t(link.text, link.textKo)}</span>
        <span className="caret" />
      </button>
      <ul className="dropdown-menu">
        {filtered.map((sublink, i) => {
          // While the dropdown is open from hovering the button, and before the user
          // has moved onto a specific item, hint which one the button's click will
          // jump to (see firstSublink above).
          const showActiveHint = menuStyle === 'hover' && open && hoveredSubIdx === null && sublink === firstSublink;
          return sublink.separator ? (
            <li key={i} className="dropdown-menu-divider" role="separator" />
          ) : sublink.sectionTitle ? (
            <li key={i} className="dropdown-menu-section-title">
              {t(sublink.text, sublink.textKo)}
            </li>
          ) : (
            <li
              key={i}
              onMouseEnter={() => setHoveredSubIdx(i)}
              onMouseLeave={() => setHoveredSubIdx((idx) => (idx === i ? null : idx))}
            >
              {sublink.target === '_blank' ? (
                <a
                  href={typeof sublink.href === 'function' ? sublink.href(user) : sublink.href ?? '#'}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={showActiveHint ? 'header-dropdown-link--active-hint' : undefined}
                >
                  <i className={getIconClass(sublink.icon)} /> {t(sublink.text, sublink.textKo)}
                  <i className="fa fa-external-link header-dropdown-external-icon" aria-hidden />
                </a>
              ) : (
                <Link
                  to={resolveHref(sublink, user)}
                  onClick={() => setOpen(false)}
                  className={showActiveHint ? 'header-dropdown-link--active-hint' : undefined}
                >
                  <i className={getIconClass(sublink.icon)} /> {t(sublink.text, sublink.textKo)}
                </Link>
              )}
            </li>
          );
        })}
      </ul>
    </li>
  );
}
